import * as i1 from '@volo/ngx-lepton-x.core';
import { DataStore, StyleService, WINDOW, createStyleFactory, LPX_TRANSLATE_TOKEN, LpxBrandLogoModule, sortItems, LPX_INITIAL_STYLES, LPX_LAYOUT_STYLE_FINAL, styleLoadFactory, LPX_STYLE_FINAL, LpxCoreModule } from '@volo/ngx-lepton-x.core';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, APP_INITIALIZER, NgModule, Component, ContentChild, ViewEncapsulation, ChangeDetectionStrategy, HostBinding, Directive, Input, HostListener } from '@angular/core';
import { fromEvent } from 'rxjs';
import { map, startWith, filter, takeWhile, distinctUntilKeyChanged } from 'rxjs/operators';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@angular/platform-browser';

var StyleNames;
(function (StyleNames) {
    StyleNames["Dark"] = "dark";
    StyleNames["Dim"] = "dim";
    StyleNames["Light"] = "light";
    StyleNames["System"] = "system";
})(StyleNames || (StyleNames = {}));
var BundleNames;
(function (BundleNames) {
    BundleNames["BootstrapDark"] = "bootstrap-dark";
    BundleNames["BootstrapDim"] = "bootstrap-dim";
    BundleNames["BootstrapLight"] = "bootstrap-light";
    BundleNames["Dark"] = "dark";
    BundleNames["Dim"] = "dim";
    BundleNames["Light"] = "light";
    BundleNames["Ng"] = "ng-bundle";
})(BundleNames || (BundleNames = {}));

var ThemeTranslateKeys;
(function (ThemeTranslateKeys) {
    ThemeTranslateKeys["AppearanceTitle"] = "theme.settings.title";
    ThemeTranslateKeys["DarkMode"] = "theme.settings.darkMode";
    ThemeTranslateKeys["LightMode"] = "theme.settings.lightMode";
    ThemeTranslateKeys["SemiDarkMode"] = "theme.settings.semiDarkMode";
    ThemeTranslateKeys["System"] = "theme.settings.system";
})(ThemeTranslateKeys || (ThemeTranslateKeys = {}));
const ThemeTranslateDefaults = {
    [ThemeTranslateKeys.AppearanceTitle]: 'Appearance',
    [ThemeTranslateKeys.DarkMode]: 'Dark',
    [ThemeTranslateKeys.LightMode]: 'Light',
    [ThemeTranslateKeys.SemiDarkMode]: 'Semi-Dark',
    [ThemeTranslateKeys.System]: 'System',
};

class LpxTheme {
    constructor(args) {
        this.bundles = args.bundles;
        this.styleName = args.styleName;
        this.defaultTheme = args.defaultTheme;
        this.label = args.label;
        this.icon = args.icon;
        this.order = args.order;
        this.systemKey = args.systemKey;
    }
    updateBundle(bundle) {
        const updateIndex = this.bundles.findIndex((b) => bundle.bundleName === b.bundleName);
        if (updateIndex > -1) {
            this.bundles[updateIndex] = bundle;
        }
    }
    addBundle(bundle) {
        this.bundles.push(bundle);
    }
    deleteBundle(bundle) {
        const deleteIndex = this.bundles.findIndex((b) => bundle.bundleName === b.bundleName);
        if (deleteIndex > -1) {
            this.bundles.splice(deleteIndex, 1);
        }
    }
}

const dimTheme = new LpxTheme({
    bundles: [
        {
            bundleName: BundleNames.BootstrapDim,
        },
        {
            bundleName: BundleNames.Dim,
        },
    ],
    styleName: StyleNames.Dim,
    icon: 'bi bi-brightness-alt-high-fill',
    label: ThemeTranslateKeys.SemiDarkMode,
});
const darkTheme = new LpxTheme({
    bundles: [
        {
            bundleName: BundleNames.BootstrapDark,
        },
        {
            bundleName: BundleNames.Dark,
        },
    ],
    styleName: StyleNames.Dark,
    systemKey: StyleNames.Dark,
    icon: 'bi bi-moon-fill',
    label: ThemeTranslateKeys.DarkMode,
});
const lightTheme = new LpxTheme({
    bundles: [
        {
            bundleName: BundleNames.BootstrapLight,
        },
        {
            bundleName: BundleNames.Light,
        },
    ],
    systemKey: StyleNames.Light,
    styleName: StyleNames.Light,
    icon: 'bi bi-sun-fill',
    label: ThemeTranslateKeys.LightMode,
});
const LPX_THEME_DEFAULTS = {
    localStorageKey: 'LPX_THEME',
    disableSystemOption: false,
};
const LPX_THEME_STYLES_DEFAULTS = [
    lightTheme,
    dimTheme,
    darkTheme,
];
const ThemeLinkElemId = 'lpxTheme';

const LPX_THEME_OPTIONS = new InjectionToken('LPX_THEME_SETTINGS');
const LPX_THEMES = new InjectionToken('LPX_THEMES');
const LPX_THEMES_FINAL = new InjectionToken('LPX_THEMES_FINAL');

class ThemeService {
    get selectedStyle() {
        return this.store.state.selectedStyle;
    }
    get currentSystemTheme() {
        return this.systemMediaQueryList.matches ? 'dark' : 'light';
    }
    constructor(themeSettings, styles, injector, window, languageService, styleService, localStorageService) {
        this.themeSettings = themeSettings;
        this.styles = styles;
        this.injector = injector;
        this.window = window;
        this.languageService = languageService;
        this.styleService = styleService;
        this.localStorageService = localStorageService;
        this.store = new DataStore({
            styles: [],
            selectedStyle: {},
        });
        this.id = 'styles';
        this.convertThemeToNavbarItem = (styles) => {
            return styles.map((style) => ({
                icon: style.icon,
                text: style.label,
                order: style.order,
                id: style.styleName,
                selected: style.styleName === this.selectedStyle.styleName,
                action: () => {
                    if (this.selectedStyle.styleName === style.styleName) {
                        return true;
                    }
                    return this.setTheme(style);
                },
            }));
        };
        this.selectedStyle$ = this.store.sliceState((state) => state.selectedStyle);
        this.styles$ = this.store.sliceState((state) => state.styles);
        this.stylesAsNavbarItems$ = this.styles$.pipe(map(this.convertThemeToNavbarItem));
        // TODO: PROVIDE API
        this.stylesAsSettingsGroup$ = this.stylesAsNavbarItems$.pipe(map((styles) => ({
            text: ThemeTranslateKeys.AppearanceTitle,
            icon: 'bi bi-palette-fill',
            id: this.id,
            children: styles,
        })));
        this.systemMediaQueryList = this.window.matchMedia('(prefers-color-scheme: dark)');
        this.systemThemeChange$ = fromEvent(this.systemMediaQueryList, 'change').pipe(startWith(this.systemMediaQueryList), map((list) => list.matches), filter(() => this.selectedStyle.styleName === StyleNames.System), takeWhile(() => !this.themeSettings.disableSystemOption));
        if (!this.themeSettings.disableSystemOption) {
            this.createSystemOption();
        }
        this.subscribeDirectionChange();
    }
    getInitialTheme() {
        const defaultStyle = this.styles.find(({ defaultTheme }) => defaultTheme);
        const savedStyle = this.styles.find(({ styleName }) => styleName === this.localStorageService.getItem(this.themeSettings.localStorageKey));
        if (this.themeSettings.disableSystemOption) {
            return savedStyle || defaultStyle;
        }
        else {
            const system = this.findLpxStyleByName(StyleNames.System);
            return savedStyle || defaultStyle || system;
        }
    }
    createSystemOption() {
        const systemOption = new LpxTheme({
            styleName: StyleNames.System,
            label: ThemeTranslateKeys.System,
            icon: 'bi bi-laptop-fill',
            bundles: [],
        });
        this.styles.push(systemOption);
        this.systemThemeChange$.subscribe(() => {
            this.setTheme(systemOption);
        });
    }
    async initTheme() {
        const style = this.getInitialTheme();
        if (style) {
            await this.setTheme(style);
            this.store.patch({ styles: this.styles });
        }
        return Promise.resolve();
    }
    async setTheme(style) {
        if (!style) {
            return Promise.resolve(true);
        }
        const styleService = this.injector.get(StyleService);
        const { styleName, bundles, systemTheme } = this.getBundlesAndStyle(style);
        for (const bundle of bundles) {
            await styleService.loadStyle(bundle, this.languageService.selectedLanguage?.isRTL ? 'rtl' : 'ltr');
        }
        this.activateStyle(styleName, systemTheme);
        this.localStorageService.setItem(this.themeSettings.localStorageKey, style.styleName);
        this.store.patch({ selectedStyle: style });
        return Promise.resolve(true);
    }
    getBundlesAndStyle(style) {
        let styleName = style.styleName;
        let bundles = style.bundles;
        let systemTheme = this.currentSystemTheme;
        if (style.styleName === StyleNames.System) {
            const activeStyle = this.styles.find((s) => s.systemKey === this.currentSystemTheme);
            if (activeStyle) {
                styleName = activeStyle.styleName;
                bundles = activeStyle.bundles;
            }
            else {
                throw new Error(`System style ${this.currentSystemTheme} not found.
          Please add systemKey with value ${this.currentSystemTheme} to the style`);
            }
            /*
             * The reason for this opposite condition is when the user changes the system theme old theme is the opposite of the current system theme
             * */
            systemTheme = this.currentSystemTheme === 'dark' ? 'light' : 'dark';
        }
        return { styleName, bundles, systemTheme };
    }
    activateStyle(styleName, systemTheme) {
        if (this.selectedStyle.styleName === StyleNames.System) {
            const selectedStyle = this.styles.find((s) => s.systemKey === systemTheme);
            if (selectedStyle) {
                this.removeOldThemeStyles(selectedStyle.bundles);
                this.setThemeClassToBody(styleName, selectedStyle.styleName);
            }
        }
        else {
            if (this.selectedStyle.styleName) {
                this.removeOldThemeStyles(this.selectedStyle.bundles);
            }
            this.setThemeClassToBody(styleName, this.selectedStyle.styleName);
        }
    }
    findLpxStyleByName(styleName) {
        return this.styles.find((style) => style.styleName === styleName);
    }
    setThemeClassToBody(styleName, selectedStyleName) {
        const body = document.querySelector('body');
        if (body) {
            if (selectedStyleName) {
                body.classList.remove(`lpx-theme-${selectedStyleName}`);
            }
            body.classList.add(`lpx-theme-${styleName}`);
        }
    }
    removeOldThemeStyles(bundles) {
        bundles.forEach((bundle) => {
            const elem = document.querySelector(`link#${bundle.bundleName}`);
            elem?.remove();
        });
    }
    subscribeDirectionChange() {
        this.languageService.languageChange$
            .pipe(distinctUntilKeyChanged('isRTL'))
            .subscribe(async (lang) => {
            const bundles = this.selectedStyle.bundles || [];
            if (!bundles.length &&
                this.selectedStyle.styleName === StyleNames.System) {
                const selectedStyle = this.styles.find((s) => s.systemKey === this.currentSystemTheme);
                if (selectedStyle) {
                    bundles.push(...selectedStyle.bundles);
                }
            }
            const direction = lang?.isRTL ? 'rtl' : 'ltr';
            for (const bundle of this.selectedStyle.bundles || []) {
                await this.styleService.replaceStyle(bundle, direction);
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ThemeService, deps: [{ token: LPX_THEME_OPTIONS }, { token: LPX_THEMES }, { token: i0.Injector }, { token: WINDOW }, { token: i1.LanguageService }, { token: i1.StyleService }, { token: i1.LpxLocalStorageService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ThemeService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ThemeService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LPX_THEME_OPTIONS]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LPX_THEMES]
                }] }, { type: i0.Injector }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: i1.LanguageService }, { type: i1.StyleService }, { type: i1.LpxLocalStorageService }] });

function initTheme(themeService) {
    const fn = () => themeService.initTheme();
    return fn;
}
class LpxThemeModule {
    static forRoot(options, defaultTheme) {
        if (defaultTheme) {
            LPX_THEME_STYLES_DEFAULTS.forEach((theme) => {
                theme.defaultTheme = theme.styleName === defaultTheme;
            });
        }
        return {
            ngModule: LpxThemeModule,
            providers: [
                ThemeService,
                {
                    provide: LPX_THEME_OPTIONS,
                    useValue: options || LPX_THEME_DEFAULTS,
                },
                {
                    provide: LPX_THEMES,
                    useValue: LPX_THEME_STYLES_DEFAULTS,
                },
                {
                    provide: LPX_THEMES_FINAL,
                    deps: [LPX_THEMES],
                    useFactory: createStyleFactory(options?.styleFactory),
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: initTheme,
                    deps: [ThemeService],
                    multi: true,
                },
                {
                    provide: LPX_TRANSLATE_TOKEN,
                    useValue: [ThemeTranslateDefaults],
                    multi: true,
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: LpxThemeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.1.3", ngImport: i0, type: LpxThemeModule, imports: [CommonModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: LpxThemeModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: LpxThemeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }] });

class CardFooterComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: CardFooterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: CardFooterComponent, selector: "lpx-card-footer", ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: CardFooterComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'lpx-card-footer',
                    template: '<ng-content></ng-content>',
                }]
        }] });

class CardHeaderComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: CardHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: CardHeaderComponent, selector: "lpx-card-header", ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: CardHeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'lpx-card-header',
                    template: '<ng-content></ng-content>',
                }]
        }] });

class CardComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: CardComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: CardComponent, selector: "lpx-card", queries: [{ propertyName: "cardFooterTemplate", first: true, predicate: CardFooterComponent, descendants: true }, { propertyName: "cardHeaderTemplate", first: true, predicate: CardHeaderComponent, descendants: true }], ngImport: i0, template: "<div class=\"card\">\r\n  <div class=\"card-header\" *ngIf=\"cardHeaderTemplate\">\r\n    <ng-content select=\"lpx-card-header\"></ng-content>\r\n  </div>\r\n  <div class=\"card-body\">\r\n    <ng-content></ng-content>\r\n  </div>\r\n  <div class=\"card-footer\" *ngIf=\"cardFooterTemplate\">\r\n    <ng-content select=\"lpx-card-footer\"></ng-content>\r\n  </div>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: CardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'lpx-card', template: "<div class=\"card\">\r\n  <div class=\"card-header\" *ngIf=\"cardHeaderTemplate\">\r\n    <ng-content select=\"lpx-card-header\"></ng-content>\r\n  </div>\r\n  <div class=\"card-body\">\r\n    <ng-content></ng-content>\r\n  </div>\r\n  <div class=\"card-footer\" *ngIf=\"cardFooterTemplate\">\r\n    <ng-content select=\"lpx-card-footer\"></ng-content>\r\n  </div>\r\n</div>\r\n" }]
        }], propDecorators: { cardFooterTemplate: [{
                type: ContentChild,
                args: [CardFooterComponent]
            }], cardHeaderTemplate: [{
                type: ContentChild,
                args: [CardHeaderComponent]
            }] } });

const declarationsWithExports$1 = [
    CardComponent,
    CardFooterComponent,
    CardHeaderComponent,
];
class CardModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: CardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.1.3", ngImport: i0, type: CardModule, declarations: [CardComponent,
            CardFooterComponent,
            CardHeaderComponent], imports: [CommonModule], exports: [CardComponent,
            CardFooterComponent,
            CardHeaderComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: CardModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: CardModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...declarationsWithExports$1],
                    imports: [CommonModule],
                    exports: [...declarationsWithExports$1],
                }]
        }] });

class ContextMenuHeaderComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ContextMenuHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: ContextMenuHeaderComponent, selector: "lpx-context-menu-header", ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ContextMenuHeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'lpx-context-menu-header',
                    template: '<ng-content></ng-content>',
                }]
        }] });

class ContextMenuComponent {
    constructor(cdRef) {
        this.cdRef = cdRef;
        this._visible = false;
    }
    get visible() {
        return this._visible;
    }
    set visible(value) {
        this._visible = value;
        this.cdRef.detectChanges();
    }
    open() {
        this.visible = true;
    }
    close() {
        this.visible = false;
    }
    toggle(status) {
        this.visible = status !== undefined ? status : !this._visible;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ContextMenuComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: ContextMenuComponent, selector: "lpx-context-menu", queries: [{ propertyName: "header", first: true, predicate: ContextMenuHeaderComponent, descendants: true }], exportAs: ["lpx-context-menu"], ngImport: i0, template: "<div class=\"lpx-context-menu\" [class.show]=\"visible\">\r\n  <div class=\"lpx-context-menu--header\" *ngIf=\"header\">\r\n    <ng-content select=\"lpx-context-menu-header\"></ng-content>\r\n  </div>\r\n  <ng-content></ng-content>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ContextMenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'lpx-context-menu', encapsulation: ViewEncapsulation.None, exportAs: 'lpx-context-menu', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"lpx-context-menu\" [class.show]=\"visible\">\r\n  <div class=\"lpx-context-menu--header\" *ngIf=\"header\">\r\n    <ng-content select=\"lpx-context-menu-header\"></ng-content>\r\n  </div>\r\n  <ng-content></ng-content>\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { header: [{
                type: ContentChild,
                args: [ContextMenuHeaderComponent]
            }] } });

class ContextMenuActionGroupComponent {
    constructor() {
        this.hostClass = 'lpx-context-menu--action-group';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ContextMenuActionGroupComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: ContextMenuActionGroupComponent, selector: "lpx-context-menu-action-group", host: { properties: { "class": "this.hostClass" } }, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ContextMenuActionGroupComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'lpx-context-menu-action-group',
                    template: ` <ng-content></ng-content> `,
                }]
        }], propDecorators: { hostClass: [{
                type: HostBinding,
                args: ['class']
            }] } });

class ContextMenuToggleDirective {
    clickOnItem() {
        this.lpxCtx.toggle();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ContextMenuToggleDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.1.3", type: ContextMenuToggleDirective, selector: "[lpxCtxToggle]", inputs: { lpxCtx: ["lpxCtxToggle", "lpxCtx"] }, host: { listeners: { "click": "clickOnItem()" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ContextMenuToggleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[lpxCtxToggle]',
                }]
        }], propDecorators: { lpxCtx: [{
                type: Input,
                args: ['lpxCtxToggle']
            }], clickOnItem: [{
                type: HostListener,
                args: ['click']
            }] } });

const declarationsWithExports = [
    ContextMenuComponent,
    ContextMenuHeaderComponent,
    ContextMenuActionGroupComponent,
    ContextMenuToggleDirective,
];
class LpxContextMenuModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: LpxContextMenuModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.1.3", ngImport: i0, type: LpxContextMenuModule, declarations: [ContextMenuComponent,
            ContextMenuHeaderComponent,
            ContextMenuActionGroupComponent,
            ContextMenuToggleDirective], imports: [CommonModule, LpxBrandLogoModule], exports: [ContextMenuComponent,
            ContextMenuHeaderComponent,
            ContextMenuActionGroupComponent,
            ContextMenuToggleDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: LpxContextMenuModule, imports: [CommonModule, LpxBrandLogoModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: LpxContextMenuModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...declarationsWithExports],
                    exports: [...declarationsWithExports],
                    imports: [CommonModule, LpxBrandLogoModule],
                }]
        }] });

// TODO: muhammed: find out how to abstract for TobparContentService and ToolbarService
class TopbarContentService {
    constructor() {
        this.store = new DataStore({ items: [] });
        this.items$ = this.store.sliceState(({ items }) => items);
    }
    setItems(items) {
        this.store.patch({ items: items.sort(sortItems) });
    }
    addItem(item) {
        this.setItems([...this.store.state.items, item]);
    }
    patchItem(itemId, item) {
        const { items } = this.store.state;
        const index = items.findIndex(({ id }) => id === itemId);
        if (index === -1) {
            return;
        }
        const updateItems = [...items];
        updateItems[index] = { id: itemId, ...item };
        this.setItems(updateItems);
    }
    removeItem(id) {
        const { items } = this.store.state;
        const index = items.findIndex((item) => item.id === id);
        if (index === -1) {
            return;
        }
        const updateItems = [...items.slice(0, index), ...items.slice(index + 1)];
        this.store.patch({ items: updateItems });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: TopbarContentService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: TopbarContentService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: TopbarContentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class TopbarContentComponent {
    constructor(topbarContent, sanitizer, injector) {
        this.topbarContent = topbarContent;
        this.sanitizer = sanitizer;
        this.injector = injector;
        this.items$ = this.topbarContent.items$.pipe(map((items) => items.map((item) => item.html && typeof item.html === 'string'
            ? { ...item, html: this.sanitizer.bypassSecurityTrustHtml(item.html) }
            : item)));
    }
    actionClick(item) {
        if (item.action) {
            item.action();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: TopbarContentComponent, deps: [{ token: TopbarContentService }, { token: i2.DomSanitizer }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: TopbarContentComponent, selector: "lpx-topbar-content", ngImport: i0, template: "<ng-container *ngFor=\"let item of items$ | async\">\r\n  <ng-container *ngIf=\"item.component\">\r\n    <ng-container\r\n      *ngComponentOutlet=\"item.component; injector: item.injector || injector\"\r\n    ></ng-container>\r\n  </ng-container>\r\n  <ng-container *ngIf=\"item.template\" [ngTemplateOutlet]=\"item.template\"></ng-container>\r\n  <div *ngIf=\"item.html\" [innerHtml]=\"item.html\" (click)=\"actionClick(item)\"></div>\r\n</ng-container>\r\n", dependencies: [{ kind: "directive", type: i3.NgComponentOutlet, selector: "[ngComponentOutlet]", inputs: ["ngComponentOutlet", "ngComponentOutletInputs", "ngComponentOutletInjector", "ngComponentOutletContent", "ngComponentOutletNgModule", "ngComponentOutletNgModuleFactory"] }, { kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: TopbarContentComponent, decorators: [{
            type: Component,
            args: [{ selector: 'lpx-topbar-content', template: "<ng-container *ngFor=\"let item of items$ | async\">\r\n  <ng-container *ngIf=\"item.component\">\r\n    <ng-container\r\n      *ngComponentOutlet=\"item.component; injector: item.injector || injector\"\r\n    ></ng-container>\r\n  </ng-container>\r\n  <ng-container *ngIf=\"item.template\" [ngTemplateOutlet]=\"item.template\"></ng-container>\r\n  <div *ngIf=\"item.html\" [innerHtml]=\"item.html\" (click)=\"actionClick(item)\"></div>\r\n</ng-container>\r\n" }]
        }], ctorParameters: () => [{ type: TopbarContentService }, { type: i2.DomSanitizer }, { type: i0.Injector }] });

class LpxTopbarContentModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: LpxTopbarContentModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.1.3", ngImport: i0, type: LpxTopbarContentModule, declarations: [TopbarContentComponent], imports: [CommonModule], exports: [TopbarContentComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: LpxTopbarContentModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: LpxTopbarContentModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [TopbarContentComponent],
                    exports: [TopbarContentComponent],
                    imports: [CommonModule],
                }]
        }] });

const LPX_PRO_STYLE_TOKEN = new InjectionToken('LPX_PRO_STYLE_TOKEN');

// TODO: make generator function in core
const getLpxProStyleProviders = (styleFactory) => [
    {
        provide: APP_INITIALIZER,
        deps: [LPX_PRO_STYLE_TOKEN],
        multi: true,
        useValue: () => null,
    },
    {
        provide: LPX_PRO_STYLE_TOKEN,
        deps: [LPX_INITIAL_STYLES, LPX_LAYOUT_STYLE_FINAL],
        useFactory: styleLoadFactory,
    },
    {
        provide: LPX_STYLE_FINAL,
        deps: [LPX_PRO_STYLE_TOKEN],
        useFactory: createStyleFactory(styleFactory),
    },
];

class LpxModule {
    static forRoot(options) {
        return {
            ngModule: LpxModule,
            providers: [
                LpxCoreModule.forRoot(options).providers,
                LpxThemeModule.forRoot(options?.themeOptions, options?.defaultTheme)
                    .providers,
                ...getLpxProStyleProviders(options?.styleFactory),
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: LpxModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.1.3", ngImport: i0, type: LpxModule, imports: [LpxCoreModule, LpxThemeModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: LpxModule, imports: [LpxCoreModule, LpxThemeModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: LpxModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [LpxCoreModule, LpxThemeModule],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { BundleNames, CardComponent, CardFooterComponent, CardHeaderComponent, CardModule, ContextMenuActionGroupComponent, ContextMenuComponent, ContextMenuHeaderComponent, ContextMenuToggleDirective, LPX_THEMES, LPX_THEMES_FINAL, LPX_THEME_DEFAULTS, LPX_THEME_OPTIONS, LPX_THEME_STYLES_DEFAULTS, LpxContextMenuModule, LpxModule, LpxTheme, LpxThemeModule, LpxTopbarContentModule, StyleNames, ThemeLinkElemId, ThemeService, ThemeTranslateDefaults, ThemeTranslateKeys, TopbarContentComponent, TopbarContentService, darkTheme, dimTheme, initTheme, lightTheme };
